// src/components/CardSearchInputsSessionFiltered.js
import React, { useEffect, useState } from "react";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
  Alert,
  LinearProgress,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import CloseIcon from "@mui/icons-material/Close";
import CardLookupDialog from "./modules/sessions/CardLookupDialog";
import ImageCropDialog from "./modules/sessions/ImageCropDialog";
import ManualProductResearchDialog from "./modules/sessions/ManualProductResearchDialog";
import ProductResearchAgentBridgeDialog from "./modules/sessions/ProductResearchAgentBridgeDialog";

export default function CardSearchInputsSessionFiltered({ search_session_id, session_name, onSessionProgressChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCard, setNewCard] = useState("");
  const [searchType, setSearchType] = useState("Raw");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [sessionStatus, setSessionStatus] = useState("");
  const [sessionProgress, setSessionProgress] = useState({
    total_cards: 0,
    scraped_cards: 0,
    scraping_cards: 0,
    failed_cards: 0,
    pending_cards: 0,
  });

  // Find Card modal
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupInitialQuery, setLookupInitialQuery] = useState("");
  const [lookupInitialHints, setLookupInitialHints] = useState(null);
  const [suggestedCards, setSuggestedCards] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionImagePreviews, setSuggestionImagePreviews] = useState([]);
  const [suggestionModel, setSuggestionModel] = useState("");
  const [activeSuggestionId, setActiveSuggestionId] = useState(null);
  const [includeMetadataInSearch, setIncludeMetadataInSearch] = useState(false);
  const [recommendingSuggestionId, setRecommendingSuggestionId] = useState(null);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState([]);
  const [recommendationScope, setRecommendationScope] = useState("all");
  const [batchRecommendationState, setBatchRecommendationState] = useState({ running: false, mode: "", requested: 0 });
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [manualResearchOpen, setManualResearchOpen] = useState(false);
  const [manualResearchCard, setManualResearchCard] = useState(null);
  const [agentBridgeOpen, setAgentBridgeOpen] = useState(false);

  const formatUsd = (value) => {
    const num = Number(value || 0);
    if (!num) return "$0.000000";
    return `$${num.toFixed(6)}`;
  };


  useEffect(() => {
    fetchInputs();
    fetchSavedSuggestions();
  }, [search_session_id]);

  useEffect(() => () => {
    suggestionImagePreviews.forEach((item) => {
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
  }, [suggestionImagePreviews]);

  useEffect(() => {
    if (!search_session_id) return undefined;
    const interval = setInterval(() => {
      fetchInputs({ keepLoader: true });
    }, 2500);
    return () => clearInterval(interval);
  }, [search_session_id]);

  const fetchSavedSuggestions = async () => {
    if (!search_session_id) return;
    try {
      const res = await fetch(`/api/cards/lookup/suggestions?search_session_id=${search_session_id}`);
      const json = await res.json();
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to load saved suggestions");
      }
      const loadedSuggestions = Array.isArray(json?.data) ? json.data : [];
      setSuggestedCards(loadedSuggestions);
      setSelectedSuggestionIds((prev) => prev.filter((id) => loadedSuggestions.some((item) => item.id === id)));
    } catch (err) {
      console.error("Failed to load saved suggestions", err);
    }
  };

  const recommendationHighOnly = recommendationScope === "high_only";

  const toggleSuggestionSelected = (id) => {
    setSelectedSuggestionIds((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));
  };

  const selectableSuggestions = suggestedCards.filter((item) => item.review_status !== "rejected");

  const selectAllSuggestions = () => {
    setSelectedSuggestionIds(selectableSuggestions.map((item) => item.id));
  };

  const clearSelectedSuggestions = () => {
    setSelectedSuggestionIds([]);
  };

  const patchSuggestion = async (id, payload) => {
    const res = await fetch(`/api/cards/lookup/suggestions/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await res.json();
    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || "Failed to update suggestion");
    }
    const updated = json?.data;
    setSuggestedCards((prev) => prev.map((item) => (item.id === id ? { ...item, ...updated } : item)));
    return updated;
  };



  const recommendBestMatch = async (suggestion) => {
    if (!suggestion?.id) return;
    try {
      setRecommendingSuggestionId(suggestion.id);
      const res = await fetch(`/api/cards/lookup/suggestions/${suggestion.id}/recommend-best-match?high_only=${recommendationHighOnly ? 1 : 0}`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to recommend best match");
      }
      const updated = json?.data || null;
      if (updated) {
        setSuggestedCards((prev) => prev.map((item) => (item.id === updated.id ? { ...item, ...updated } : item)));
      }
      const recommendedName = json?.recommended_candidate?.name_and_number || updated?.recommended_card_name || "";
      setSnackbar({
        open: true,
        message: recommendedName ? `Recommended ${recommendedName}` : "Recommendation complete",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to recommend best match", err);
      setSnackbar({ open: true, message: err?.message || "Failed to recommend best match", severity: "error" });
    } finally {
      setRecommendingSuggestionId(null);
    }
  };

  const recommendSuggestionsBatch = async (mode = "selected") => {
    const selectedIds = mode === "all"
      ? selectableSuggestions.map((item) => item.id)
      : selectedSuggestionIds.filter((id) => suggestedCards.some((item) => item.id === id && item.review_status !== "rejected"));

    if (!selectedIds.length) {
      setSnackbar({
        open: true,
        message: mode === "all" ? "No suggestions available to recommend." : "Select at least one suggestion first.",
        severity: "warning",
      });
      return;
    }

    try {
      setBatchRecommendationState({ running: true, mode, requested: selectedIds.length });
      const res = await fetch("/api/cards/lookup/suggestions/recommend-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode,
          search_session_id,
          suggestion_ids: selectedIds,
          high_only: recommendationHighOnly,
        }),
      });
      const json = await res.json();
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to recommend suggestions");
      }

      const results = Array.isArray(json?.results) ? json.results : [];
      const updatedRows = results.map((item) => item?.data).filter(Boolean);
      if (updatedRows.length) {
        setSuggestedCards((prev) => prev.map((item) => {
          const updated = updatedRows.find((row) => row.id === item.id);
          return updated ? { ...item, ...updated } : item;
        }));
      }

      const summary = json?.summary || {};
      setSnackbar({
        open: true,
        message: `Recommendations finished: ${summary.recommended || 0} recommended, ${summary.failed || 0} failed, ${summary.skipped || 0} skipped`,
        severity: summary.failed ? "warning" : "success",
      });

      if (mode === "all") {
        setSelectedSuggestionIds([]);
      }
    } catch (err) {
      console.error("Failed to recommend suggestions", err);
      setSnackbar({ open: true, message: err?.message || "Failed to recommend suggestions", severity: "error" });
    } finally {
      setBatchRecommendationState({ running: false, mode: "", requested: 0 });
    }
  };

  const acceptRecommendation = async (item) => {
    if (!item?.id || !item?.recommended_card_name) return;
    try {
      await addCardToSession(item.recommended_card_name);
      const res = await fetch(`/api/cards/lookup/suggestions/${item.id}/accept-recommendation`, { method: "POST" });
      const json = await res.json();
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to accept recommendation");
      }
      const updated = json?.data || null;
      if (updated) {
        setSuggestedCards((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
      }
      setSnackbar({
        open: true,
        message: `Added ${item.recommended_card_name} to session`,
        severity: "success",
      });
      await fetchInputs({ keepLoader: true });
    } catch (err) {
      console.error("Failed to accept recommendation", err);
      setSnackbar({ open: true, message: err?.message || "Failed to accept recommendation", severity: "error" });
    }
  };

  const fetchSessionStatus = async () => {
    try {
      const res = await fetch(`/api/search-sessions/${search_session_id}`);
      const json = await res.json();
      setSessionStatus(json.status || "Unknown");
      setSessionProgress({
        total_cards: Number(json.total_cards || 0),
        scraped_cards: Number(json.scraped_cards || 0),
        scraping_cards: Number(json.scraping_cards || 0),
        failed_cards: Number(json.failed_cards || 0),
        pending_cards: Number(json.pending_cards || 0),
      });
      if (typeof onSessionProgressChange === "function") {
        onSessionProgressChange(json);
      }
    } catch (err) {
      console.error("Failed to fetch session status", err);
      setSessionStatus("Error");
    }
  };

  const fetchInputs = async ({ keepLoader = false } = {}) => {
    if (!keepLoader) {
      setLoading(true);
    }
    try {
      const res = await fetch(`/api/card-search-inputs?session_id=${search_session_id}`);
      const data = Array.isArray(res) ? [] : await res.json();

      const list = (Array.isArray(data) ? data : data?.data || []).map((row) => ({
        id: row.id,
        card_name: row.card_name,
        search_type: row.search_type,
        status: row.status,
        session_name: row.session_name,
        auto_generated_ebay_url: row.auto_generated_ebay_url || row.url || "",
        exclusion_terms: row.exclusion_terms || "",
        latest_summary_id: row.latest_summary_id,
        low_price_no_shipping: row.low_price_no_shipping,
        avg_price: row.avg_price,
        high_price_no_shipping: row.high_price_no_shipping,
        data_source: row.data_source || "",
        summary_captured_at: row.summary_captured_at || null,
        suggested_buy_price: row.suggested_buy_price,
        sourcing_score: row.sourcing_score,
        profitable_to_source: row.profitable_to_source,
        sales_count_30_actual: row.sales_count_30_actual,
        active_listings_count: row.active_listings_count,
        sell_through_rate_30: row.sell_through_rate_30,
      }));

      setRows(list);
      await fetchSessionStatus();
    } catch (err) {
      console.error("Failed to load card inputs", err);
    } finally {
      if (!keepLoader) {
        setLoading(false);
      }
    }
  };

  const addCardToSession = async (cardName) => {
    if (!cardName?.trim()) return;
    await fetch("/api/card-search-inputs", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        card_name: cardName.trim(),
        search_type: searchType,
        search_session_id,
        session_name,
      }),
    });
    await fetchInputs(); // refresh table in the background (modal stays open)
  };

  const handleAddCard = async () => {
    await addCardToSession(newCard);
    setNewCard("");
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`/api/card-search-inputs/${id}`, { method: "DELETE" });
      fetchInputs();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const updateRowStatus = async (cardId, newStatus) => {
    try {
      await fetch(`/api/update-card-status/${cardId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (err) {
      console.error("Status update failed", err);
    }
  };

  const handleScrape = async (row) => {
    try {
      const res = await fetch("/api/run-scraper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: row.id,
          card_name: row.card_name,
          url: row.auto_generated_ebay_url,
        }),
      });

      const result = await res.json();

      if (res.ok && (result.success || result.ok)) {
        setSnackbar({
          open: true,
          message: `Background scrape started for ${row.card_name}`,
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: `Failed to start card scrape: ${result.error || "Unknown error"}`,
          severity: "error",
        });
      }
    } catch (err) {
      console.error("Scrape failed", err);
      setSnackbar({
        open: true,
        message: "Scrape request failed.",
        severity: "error",
      });
    } finally {
      fetchInputs({ keepLoader: true });
    }
  };

  const openManualResearch = (row) => {
    setManualResearchCard(row);
    setManualResearchOpen(true);
  };

  const requiresManualResearch = (row) => {
    if (!row || String(row.data_source || "").startsWith("ebay_product_research_")) return false;
    const status = String(row.status || "").toLowerCase();
    return status !== "scraped" || row.sales_count_30_actual == null;
  };

  const findNextResearchCard = (currentId) => {
    const currentIndex = rows.findIndex((row) => row.id === currentId);
    const orderedCandidates = currentIndex >= 0
      ? [...rows.slice(currentIndex + 1), ...rows.slice(0, currentIndex)]
      : rows;
    return orderedCandidates.find(requiresManualResearch) || null;
  };

  const handleManualResearchSaved = async (result, { saveAndNext }) => {
    const currentId = manualResearchCard?.id;
    const nextCard = saveAndNext ? findNextResearchCard(currentId) : null;
    await fetchInputs({ keepLoader: true });

    if (saveAndNext && nextCard) {
      setManualResearchCard(nextCard);
      setManualResearchOpen(true);
      setSnackbar({
        open: true,
        message: `Saved Product Research for ${manualResearchCard?.card_name}. Moving to ${nextCard.card_name}.`,
        severity: "success",
      });
      return;
    }

    setManualResearchOpen(false);
    setManualResearchCard(null);
    setSnackbar({
      open: true,
      message: saveAndNext
        ? "Product Research saved. No incomplete cards remain."
        : result?.message || "Product Research data saved.",
      severity: "success",
    });
  };

  const openManualResearchFromBridge = (cardSearchInputId) => {
    const row = rows.find((item) => item.id === cardSearchInputId);
    if (!row) {
      setSnackbar({ open: true, message: "Card row is no longer available. Refresh the session and try again.", severity: "warning" });
      return;
    }
    setAgentBridgeOpen(false);
    openManualResearch(row);
  };

  const compressImageForSuggest = (file) => new Promise((resolve, reject) => {
    if (!file || !file.type || !file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      try {
        const maxDimension = 2200;
        const quality = 0.82;
        let { width, height } = img;

        if (!width || !height) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }

        const scale = Math.min(1, maxDimension / Math.max(width, height));
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          URL.revokeObjectURL(objectUrl);
          resolve(file);
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            resolve(file);
            return;
          }

          const originalBaseName = (file.name || "image").replace(/\.[^.]+$/, "");
          const outputName = `${originalBaseName}.jpg`;
          const compressedFile = new File([blob], outputName, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve(compressedFile.size < file.size ? compressedFile : file);
        }, "image/jpeg", quality);
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });

  const handleImageSuggest = async (filesOrFileList) => {
    const files = Array.from(filesOrFileList || []).filter(Boolean);
    if (!files.length) return;


    try {
      suggestionImagePreviews.forEach((item) => {
        if (item?.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });

      const nextPreviews = files.map((file, index) => ({
        id: `${file.name || "image"}-${index}-${file.size || 0}`,
        name: file.name || `image_${index + 1}`,
        previewUrl: URL.createObjectURL(file),
      }));

      setSuggestionsLoading(true);
      setSuggestionImagePreviews(nextPreviews);
      setSuggestedCards([]);
      setSelectedSuggestionIds([]);
      setSuggestionModel("");

      let finalItems = [];
      let lastModel = "";

      for (const file of files) {
        const uploadFile = await compressImageForSuggest(file);
        const formData = new FormData();
        formData.append("image", uploadFile, uploadFile.name || file.name || "image.jpg");
        formData.append("search_session_id", String(search_session_id));
        formData.append("max_cards", "12");

        const res = await fetch("/api/cards/lookup/suggest-from-image", {
          method: "POST",
          body: formData,
        });

        const contentType = res.headers.get("content-type") || "";
        if (!contentType.toLowerCase().includes("application/json")) {
          throw new Error(`Server returned non-JSON response for ${uploadFile.name || file.name || "image"}. This is usually caused by a large mobile photo being blocked before it reaches Flask.`);
        }

        const json = await res.json();

        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || `Failed to generate suggestions for ${uploadFile.name || file.name || "image"}`);
        }

        finalItems = Array.isArray(json?.data) ? json.data : [];
        lastModel = json?.model || lastModel;
      }

      setSuggestedCards(finalItems);
      setSuggestionModel(files.length > 1 ? `${lastModel || "Model used"} • ${files.length} images` : lastModel);
      setSnackbar({
        open: true,
        message: `Processed ${files.length} image${files.length === 1 ? "" : "s"} and loaded ${finalItems.length} suggestion${finalItems.length === 1 ? "" : "s"}` ,
        severity: "success",
      });
    } catch (err) {
      console.error("Image suggestion failed", err);
      setSnackbar({
        open: true,
        message: err?.message || "Failed to generate suggestions from image.",
        severity: "error",
      });
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const extractFirstCardNumber = (value) => {
    const text = (value || "").trim();
    if (!text) return "";
    const slashMatch = text.match(/\b(\d{1,3})\s*\/\s*\d{1,3}\b/);
    if (slashMatch) return slashMatch[1] || "";
    const plainMatch = text.match(/\b(\d{1,3})\b/);
    return plainMatch ? plainMatch[1] : "";
  };

  const buildSuggestionNameOnlyQuery = (suggestion) => {
    if (!suggestion) return "";
    return (suggestion.card_name_guess || suggestion.query || suggestion.recommended_card_name || "").trim();
  };

  const buildLookupSearchText = (suggestion) => {
    if (!suggestion) return "";
    const nameOnlyQuery = buildSuggestionNameOnlyQuery(suggestion);
    const firstNumber = extractFirstCardNumber(suggestion.card_number_guess || suggestion.query || suggestion.recommended_card_name || "");
    if (nameOnlyQuery && firstNumber) {
      return `${nameOnlyQuery} ${firstNumber}`.trim();
    }
    if (!includeMetadataInSearch) return nameOnlyQuery;

    const parts = [
      nameOnlyQuery,
      firstNumber,
      suggestion.rarity_guess || "",
      suggestion.set_name_guess || "",
      suggestion.series_name_guess || "",
    ]
      .map((part) => (part || "").trim())
      .filter(Boolean);

    return parts.join(" ").trim() || nameOnlyQuery;
  };

  const openLookupForSuggestion = (suggestionOrQuery, suggestionId = null) => {
    const suggestion = typeof suggestionOrQuery === "object" && suggestionOrQuery !== null ? suggestionOrQuery : null;
    const query = suggestion ? buildLookupSearchText(suggestion) : suggestionOrQuery;
    setLookupInitialQuery(query || "");
    setLookupInitialHints(suggestion ? {
      guessed_name: buildSuggestionNameOnlyQuery(suggestion) || "",
      guessed_number: extractFirstCardNumber(suggestion.card_number_guess || suggestion.query || suggestion.recommended_card_name || ""),
      guessed_rarity: includeMetadataInSearch ? (suggestion.rarity_guess || "") : "",
      guessed_set_name: includeMetadataInSearch ? (suggestion.set_name_guess || "") : "",
      guessed_series_name: includeMetadataInSearch ? (suggestion.series_name_guess || "") : "",
      language_guess: suggestion.language_guess || "",
    } : null);
    setActiveSuggestionId(suggestion ? suggestion.id : suggestionId);
    setLookupOpen(true);
  };

  const updateSuggestionQuery = (id, query) => {
    setSuggestedCards((prev) => prev.map((item) => (item.id === id ? { ...item, query } : item)));
  };

  const saveSuggestionQuery = async (id, query) => {
    if (!id || !query?.trim()) return;
    try {
      await patchSuggestion(id, { query: query.trim() });
    } catch (err) {
      console.error("Failed to save suggestion query", err);
      setSnackbar({
        open: true,
        message: err?.message || "Failed to save suggestion edit.",
        severity: "error",
      });
    }
  };

  const markSuggestionRejected = async (id) => {
    try {
      await patchSuggestion(id, { review_status: "rejected" });
    } catch (err) {
      console.error("Failed to reject suggestion", err);
      setSnackbar({
        open: true,
        message: err?.message || "Failed to reject suggestion.",
        severity: "error",
      });
    }
  };

  const clearSuggestions = () => {
    setSuggestionModel("");
    setSelectedSuggestionIds([]);
    suggestionImagePreviews.forEach((item) => {
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl);
      }
    });
    setSuggestionImagePreviews([]);
  };

  const handleAddSuggestedCard = async (item) => {
    await addCardToSession(item?.query || "");
    try {
      await patchSuggestion(item?.id, {
        review_status: "added_to_session",
        matched_card_name: item?.query || "",
      });
    } catch (err) {
      console.error("Failed to update suggestion after add", err);
    }
  };

  const handleScrapeSession = async () => {
    try {
      const res = await fetch("/api/run-scraper", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ search_session_id }),
      });
      const json = await res.json();
      if (res.ok && (json.ok || json.success)) {
        setSessionStatus("scraping");
        setSnackbar({
          open: true,
          message: `Background scrape started for session #${search_session_id}`,
          severity: "success",
        });
        await fetchInputs({ keepLoader: true });
      } else {
        setSnackbar({
          open: true,
          message: `Failed to start session scrape: ${json.error || "Unknown error"}`,
          severity: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Error starting session scrape.",
        severity: "error",
      });
    }
  };

  const moneyFormatter = (value) => {
    if (value == null || value === "") return "";
    return `$${Number(value).toFixed(2)}`;
  };

  const percentFormatter = (value) => {
    if (value == null || value === "") return "";
    return `${Number(value).toFixed(1)}%`;
  };

  const totalCards = Number(sessionProgress.total_cards || 0);
  const scrapedCards = Number(sessionProgress.scraped_cards || 0);
  const failedCards = Number(sessionProgress.failed_cards || 0);
  const processedCards = scrapedCards + failedCards;
  const progressPercent = totalCards > 0 ? Math.min(100, Math.round((processedCards / totalCards) * 100)) : 0;
  const isSessionScraping = String(sessionStatus || '').toLowerCase() === 'scraping' || Number(sessionProgress.scraping_cards || 0) > 0;

  const renderSessionActionBar = (suffix = "top") => {
    const inputId = `new-card-name-${suffix}`;
    const typeId = `card-type-${suffix}`;
    return (
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          id={inputId}
          sx={{ minWidth: 280, flex: 1 }}
          label="New Card Name"
          value={newCard}
          onChange={(e) => setNewCard(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleAddCard();
          }}
        />
        <FormControl sx={{ minWidth: 140 }}>
          <InputLabel id={`${typeId}-label`}>Type</InputLabel>
          <Select
            labelId={`${typeId}-label`}
            id={typeId}
            value={searchType}
            label="Type"
            onChange={(e) => setSearchType(e.target.value)}
          >
            <MenuItem value="Raw">Raw</MenuItem>
            <MenuItem value="Graded">Graded</MenuItem>
            <MenuItem value="RAW 151">RAW 151</MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={handleAddCard} disabled={!newCard.trim()}>
          Add Card
        </Button>
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => {
            setLookupInitialQuery("");
            setLookupInitialHints(null);
            setActiveSuggestionId(null);
            setLookupOpen(true);
          }}
          disabled={isSessionScraping}
        >
          Find Card
        </Button>
        <Button
          variant="outlined"
          onClick={() => setCropDialogOpen(true)}
          disabled={isSessionScraping || suggestionsLoading}
        >
          Crop Image
        </Button>
        <Button
          variant="outlined"
          component="label"
          startIcon={<ImageSearchIcon />}
          disabled={isSessionScraping || suggestionsLoading}
        >
          Suggest From Image
          <input
            hidden
            accept="image/*"
            type="file"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              if (files.length) {
                handleImageSuggest(files);
              }
              e.target.value = "";
            }}
          />
        </Button>
        <Button
          variant="outlined"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setAgentBridgeOpen(true)}
          disabled={isSessionScraping}
        >
          Agent Product Research
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleScrapeSession}
          disabled={isSessionScraping}
        >
          Scrape Session
        </Button>
      </Box>
    );
  };

  const sourceFormatter = (value) => {
    if (value === "ebay_product_research_manual") return "Manual PR";
    if (value === "ebay_product_research_agent") return "Agent PR";
    if (value) return value;
    return "";
  };

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "card_name", headerName: "Card Name", width: 300 },
    { field: "search_type", headerName: "Type", width: 130 },
    { field: "status", headerName: "Status", width: 120 },
    {
      field: "avg_price",
      headerName: "Avg 30",
      width: 110,
      renderCell: (params) => moneyFormatter(params.value),
    },
    {
      field: "suggested_buy_price",
      headerName: "Suggested Buy",
      width: 130,
      renderCell: (params) => moneyFormatter(params.value),
    },
    {
      field: "sales_count_30_actual",
      headerName: "Sold 30d",
      width: 105,
    },
    {
      field: "active_listings_count",
      headerName: "Active",
      width: 95,
    },
    {
      field: "sell_through_rate_30",
      headerName: "Sell-through 30d",
      width: 145,
      renderCell: (params) => percentFormatter(params.value),
    },
    {
      field: "sourcing_score",
      headerName: "Source Score",
      width: 120,
      renderCell: (params) => percentFormatter(params.value),
    },
    {
      field: "data_source",
      headerName: "Source",
      width: 120,
      renderCell: (params) => sourceFormatter(params.value),
    },
    { field: "session_name", headerName: "Session", width: 180 },
    {
      field: "manual_research",
      headerName: "Product Research",
      sortable: false,
      filterable: false,
      width: 175,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => openManualResearch(params.row)}
          disabled={isSessionScraping || String(params.row.status || "").toLowerCase() === "scraping"}
        >
          Enter Research Data
        </Button>
      ),
    },
    {
      field: "actions",
      type: "actions",
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<PlayArrowIcon />}
          label="Scrape"
          onClick={() => handleScrape(params.row)}
          disabled={isSessionScraping || String(params.row.status || "").toLowerCase() === "scraping"}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
          disabled={isSessionScraping || String(params.row.status || "").toLowerCase() === "scraping"}
        />,
      ],
    },
  ];

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" gutterBottom>
          Card Search Inputs (Session #{search_session_id})
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
          Session Status: {sessionStatus}
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5, gap: 2, flexWrap: "wrap" }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Scrape Progress: {scrapedCards}/{totalCards} scraped
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Scraping: {sessionProgress.scraping_cards || 0} · Failed: {failedCards} · Pending: {sessionProgress.pending_cards || 0}
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={progressPercent} />
      </Box>

      {renderSessionActionBar("top")}

      {(suggestionsLoading || suggestedCards.length > 0 || suggestionImagePreviews.length > 0) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderStyle: "dashed" }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }}>
            {suggestionImagePreviews.length > 0 ? (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}>
                {suggestionImagePreviews.map((item) => (
                  <Box key={item.id} sx={{ width: { xs: "100%", sm: 120 } }}>
                    <Box
                      component="img"
                      src={item.previewUrl}
                      alt={item.name || "Supplier upload"}
                      sx={{ width: "100%", height: 150, objectFit: "contain", borderRadius: 1, border: "1px solid #ddd", bgcolor: "#fafafa" }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, wordBreak: "break-word" }}>
                      {item.name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1, flexWrap: "wrap" }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Suggested cards from image
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review each saved suggestion, then open Find Card to confirm against your in-app image comparison.
                  </Typography>
                  {suggestionImagePreviews.length ? (
                    <Typography variant="caption" color="text.secondary">
                      {suggestionImagePreviews.length} selected image{suggestionImagePreviews.length === 1 ? "" : "s"}
                    </Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  {suggestionModel ? <Chip size="small" icon={<AutoAwesomeIcon />} label={`Suggest: ${suggestionModel}`} /> : null}
                  {suggestedCards.length ? <Chip size="small" label={`${suggestedCards.length} saved`} /> : null}
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={<Checkbox size="small" checked={includeMetadataInSearch} onChange={(e) => setIncludeMetadataInSearch(e.target.checked)} />}
                    label={<Typography variant="caption">Include metadata in search</Typography>}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="recommendation-scope-label">Recommend scope</InputLabel>
                    <Select
                      labelId="recommendation-scope-label"
                      value={recommendationScope}
                      label="Recommend scope"
                      onChange={(e) => setRecommendationScope(e.target.value)}
                    >
                      <MenuItem value="all">All rarities</MenuItem>
                      <MenuItem value="high_only">High rarity only</MenuItem>
                    </Select>
                  </FormControl>
                  <Button size="small" variant="outlined" onClick={selectAllSuggestions} disabled={!selectableSuggestions.length || batchRecommendationState.running}>Select All</Button>
                  <Button size="small" color="inherit" onClick={clearSelectedSuggestions} disabled={!selectedSuggestionIds.length || batchRecommendationState.running}>Clear Selected</Button>
                  <Button size="small" variant="outlined" onClick={() => recommendSuggestionsBatch("selected")} disabled={!selectedSuggestionIds.length || batchRecommendationState.running}>
                    {batchRecommendationState.running && batchRecommendationState.mode === "selected" ? `Recommending ${batchRecommendationState.requested}...` : `Recommend Selected (${selectedSuggestionIds.length})`}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => recommendSuggestionsBatch("all")} disabled={!selectableSuggestions.length || batchRecommendationState.running}>
                    {batchRecommendationState.running && batchRecommendationState.mode === "all" ? `Recommending ${batchRecommendationState.requested}...` : `Recommend All (${selectableSuggestions.length})`}
                  </Button>
                  <Button size="small" color="inherit" onClick={clearSuggestions} startIcon={<CloseIcon />}>Clear</Button>
                </Stack>
              </Box>

              {suggestionsLoading ? (
                <Box sx={{ py: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>Analysing image…</Typography>
                  <LinearProgress />
                </Box>
              ) : (
                <List dense sx={{ width: "100%" }}>
                  {suggestedCards.map((item) => {
                    const isSelected = selectedSuggestionIds.includes(item.id);
                    const rowBusy = recommendingSuggestionId === item.id || batchRecommendationState.running;
                    return (
                    <ListItem
                      key={item.id}
                      secondaryAction={
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => recommendBestMatch(item)}
                            disabled={rowBusy}
                          >
                            {recommendingSuggestionId === item.id ? "Recommending..." : "Recommend Best"}
                          </Button>
                          <Button size="small" variant="outlined" onClick={() => openLookupForSuggestion(item, item.id)} disabled={batchRecommendationState.running}>
                            Find Match
                          </Button>
                          <Button size="small" variant="contained" onClick={() => handleAddSuggestedCard(item)} disabled={!item.query.trim() || batchRecommendationState.running}>
                            Add as Typed
                          </Button>
                          <Button size="small" color="inherit" onClick={() => markSuggestionRejected(item.id)} disabled={item.review_status === "rejected" || batchRecommendationState.running}>
                            Reject
                          </Button>
                        </Stack>
                      }
                      sx={{ alignItems: "flex-start", pr: { xs: 1, sm: 24 }, borderBottom: "1px solid #eee" }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleSuggestionSelected(item.id)}
                              disabled={item.review_status === "rejected" || batchRecommendationState.running}
                            />
                            <TextField
                              size="small"
                              fullWidth
                              label="Suggested query"
                              value={item.query}
                              onChange={(e) => updateSuggestionQuery(item.id, e.target.value)}
                              onBlur={(e) => saveSuggestionQuery(item.id, e.target.value)}
                              disabled={batchRecommendationState.running}
                            />
                            <Chip size="small" variant="outlined" label={`#${item.display_order || 0}`} />
                            <Chip size="small" label={`${Math.round((item.confidence || 0) * 100)}%`} />
                            <Chip size="small" color={item.review_status === "added_to_session" ? "success" : item.review_status === "rejected" ? "default" : "primary"} label={item.review_status || "pending"} />
                          </Stack>
                        }
                        secondary={
                          <Box sx={{ mt: 0.75 }}>
                            {item.card_name_guess || item.card_number_guess || item.rarity_guess || item.set_name_guess || item.series_name_guess ? (
                              <>
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                                  Guess: {[item.card_name_guess, item.card_number_guess, item.rarity_guess, item.set_name_guess || item.series_name_guess].filter(Boolean).join(" • ")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: item.notes ? 0.5 : 0 }}>
                                  Find Card search: {buildLookupSearchText(item) || item.query}
                                </Typography>
                              </>
                            ) : null}
                            {item.matched_card_name ? (
                              <Typography variant="caption" color="success.main" component="div" sx={{ mb: item.notes ? 0.5 : 0 }}>
                                Matched: {item.matched_card_name}
                              </Typography>
                            ) : null}
                            {item.recommended_card_name ? (
                              <Box sx={{ mt: 0.75, mb: 0.75, display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
                                {item.recommended_image_url ? (
                                  <Box
                                    component="img"
                                    src={item.recommended_image_url}
                                    alt={item.recommended_card_name}
                                    sx={{ width: 72, height: 100, objectFit: "cover", borderRadius: 1, border: "1px solid #ddd" }}
                                  />
                                ) : null}
                                <Box sx={{ minWidth: 220 }}>
                                  <Typography variant="caption" color="primary.main" component="div" sx={{ fontWeight: 600 }}>
                                    Recommended: {item.recommended_card_name}{item.recommendation_confidence ? ` (${Math.round(item.recommendation_confidence * 100)}%)` : ""}
                                  </Typography>
                                  {(item.recommended_set_name || item.recommended_series_name || item.recommended_rarity) ? (
                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: item.recommendation_notes ? 0.5 : 0 }}>
                                      {[item.recommended_set_name, item.recommended_series_name, item.recommended_rarity].filter(Boolean).join(" • ")}
                                    </Typography>
                                  ) : null}
                                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, mb: 0.5, flexWrap: "wrap" }}>
                                    {item.recommendation_model ? <Chip size="small" variant="outlined" label={`Model: ${item.recommendation_model}`} /> : null}
                                    {item.recommendation_total_tokens ? <Chip size="small" variant="outlined" label={`Tokens: ${item.recommendation_total_tokens}`} /> : null}
                                    {(item.recommendation_cost_usd || item.recommendation_cost_usd === 0) ? <Chip size="small" color="secondary" variant="outlined" label={`Cost: ${formatUsd(item.recommendation_cost_usd)}`} /> : null}
                                  </Stack>
                                  {item.recommendation_notes ? (
                                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.75 }}>
                                      Why: {item.recommendation_notes}
                                    </Typography>
                                  ) : null}
                                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      onClick={() => acceptRecommendation(item)}
                                      disabled={item.review_status === "added_to_session" || batchRecommendationState.running}
                                    >
                                      Accept Recommendation
                                    </Button>
                                  </Stack>
                                </Box>
                              </Box>
                            ) : null}
                            {item.notes ? <Box>{item.notes}</Box> : null}
                          </Box>
                        }
                      />
                    </ListItem>
                  );})}
                </List>
              )}
            </Box>
          </Stack>
        </Paper>
      )}

      {(suggestionsLoading || suggestedCards.length > 0 || suggestionImagePreviews.length > 0) ? renderSessionActionBar("bottom") : null}

      <div style={{ height: 560, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          disableRowSelectionOnClick
          slots={{
            toolbar: () => (
              <GridToolbarContainer>
                <GridToolbarExport csvOptions={{ fileName: `session_${search_session_id}_cards` }} />
              </GridToolbarContainer>
            ),
          }}
        />
      </div>

      <ImageCropDialog
        open={cropDialogOpen}
        onClose={() => setCropDialogOpen(false)}
        onUseCrops={handleImageSuggest}
      />

      <CardLookupDialog
        open={lookupOpen}
        initialQuery={lookupInitialQuery}
        initialSearchHints={lookupInitialHints}
        onClose={() => {
          setLookupOpen(false);
          setActiveSuggestionId(null);
          setLookupInitialHints(null);
        }}
        onAdd={async (card) => {
          const matchedName = card?.name_and_number || "";
          await addCardToSession(matchedName);
          if (activeSuggestionId) {
            try {
              await patchSuggestion(activeSuggestionId, {
                review_status: "added_to_session",
                matched_card_name: matchedName,
              });
            } catch (err) {
              console.error("Failed to update matched suggestion", err);
            }
          }
          setActiveSuggestionId(null);
          setLookupInitialHints(null);
        }}
        onPick={(card) => {
          setNewCard(card?.name_and_number || "");
        }}
      />

      <ManualProductResearchDialog
        open={manualResearchOpen}
        card={manualResearchCard}
        onClose={() => {
          setManualResearchOpen(false);
          setManualResearchCard(null);
        }}
        onSaved={handleManualResearchSaved}
      />

      <ProductResearchAgentBridgeDialog
        open={agentBridgeOpen}
        searchSessionId={search_session_id}
        onClose={() => setAgentBridgeOpen(false)}
        onChanged={() => fetchInputs({ keepLoader: true })}
        onOpenManual={openManualResearchFromBridge}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
